// Typed conflict detection for fay mutations. Fay rejects a write whose target
// moved underneath the user with a typed GraphQL error code. Those are refetch
// signals — "the row you're looking at is outdated" — not user-facing failures,
// so callers branch to a neutral refresh instead of an error dead-end.

/** `triageFinding` conflicts: STALE_FINDING (a successor finding exists) and
 *  FINDING_WITHDRAWN (the finding's document was removed from the package). */
export const TRIAGE_CONFLICT_CODES: ReadonlySet<string> = new Set([
  "STALE_FINDING",
  "FINDING_WITHDRAWN",
]);

/** `removeDocumentFromPackage` conflicts: BINDING_REPLACED (the binding was
 *  superseded by a re-upload — the row is stale) and NOT_FOUND (the binding is
 *  gone entirely, e.g. removed in another tab). Both resolve by refetching;
 *  note an idempotent repeat removal returns success server-side, so NOT_FOUND
 *  here really means "never yours to remove". */
export const REMOVE_DOCUMENT_CONFLICT_CODES: ReadonlySet<string> = new Set([
  "BINDING_REPLACED",
  "NOT_FOUND",
]);

type GraphQLErrorLike = { extensions?: Record<string, unknown> };
type ApolloErrorLike = { graphQLErrors?: readonly GraphQLErrorLike[] };

/** Narrows an `unknown` catch value to something carrying `graphQLErrors`,
 *  without an `as` assertion. */
function isApolloErrorLike(error: unknown): error is ApolloErrorLike {
  return typeof error === "object" && error !== null && "graphQLErrors" in error;
}

/** True when the thrown Apollo error carries a GraphQL error whose
 *  `extensions.code` is one of `codes`. Total over `unknown` — the catch slot
 *  can receive non-Apollo errors. */
export function hasConflictCode(error: unknown, codes: ReadonlySet<string>): boolean {
  if (!isApolloErrorLike(error)) {
    return false;
  }
  return (
    error.graphQLErrors?.some((gqlError) => {
      const code = gqlError.extensions?.code;
      return typeof code === "string" && codes.has(code);
    }) ?? false
  );
}
